import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, useTheme, IconButton, Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import tcServiceCategoryService from '../../services/tc-service-category.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';

const f = { xs: '0.75rem', sm: '0.8125rem' };
const NAME_MAX = 50;

// Add a category, or edit one when `category` is given. Renaming is safe: services link by id.
const CategoryDialog = ({ open, onClose, onSave, category = null }) => {
  const isEdit = !!category;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(category?.name || '');
    setDescription(category?.description || '');
    setError(null);
  }, [open, category]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), description: description.trim() || null });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: f.sm }}>{isEdit ? 'Edit Category' : 'Add Category'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: f.xs }}>{error}</Alert>}
        <TextField
          fullWidth label="Name" value={name} required size="small" autoFocus
          onChange={e => setName(e.target.value)}
          inputProps={{ maxLength: NAME_MAX }}
          sx={{ mt: 1, mb: 1.5 }}
          InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }}
        />
        <TextField
          fullWidth label="Description (optional)" value={description} size="small" multiline rows={2}
          onChange={e => setDescription(e.target.value)}
          InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontSize: f.xs }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()} sx={{ fontSize: f.xs }}>
          {isEdit ? (saving ? 'Saving...' : 'Save') : (saving ? 'Adding...' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ServiceCategories = ({ refreshKey = 0, onChanged, onSeed, seeding, canWrite: canWriteFromParent }) => {
  // Session user for permission checks (this tab receives no currentUser prop).
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const canWrite = can(sessionUser, 'service_catalog', 'write');
  const theme = useTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reloads on mount and whenever the parent seeds default categories (refreshKey bump).
  useEffect(() => { loadData(); }, [refreshKey]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); }
  }, [success]);

  const loadData = async () => {
    setLoading(true);
    try {
      setCategories(await tcServiceCategoryService.getAll());
    } catch (err) {
      setError('Failed to load service categories');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setEditTarget(null); setDialogOpen(true); };
  const openEdit = (cat) => { setEditTarget(cat); setDialogOpen(true); };

  // Errors propagate to the dialog so it can show them (duplicate name, etc.).
  const handleSave = async (data) => {
    if (editTarget) {
      await tcServiceCategoryService.update(editTarget.id, data);
      setSuccess('Category updated');
    } else {
      await tcServiceCategoryService.create(data);
      setSuccess('Category added');
    }
    await loadData();
    onChanged && onChanged();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await tcServiceCategoryService.remove(deleteTarget.id);
      setSuccess(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      await loadData();
      onChanged && onChanged();
    } catch (err) {
      setError(err?.response?.data?.message || `Could not delete ${deleteTarget.name}`);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>
          Service Categories ({categories.length})
        </Typography>
        <Box display="flex" gap={1}>
          {onSeed && canWriteFromParent && (
            <Button variant="outlined" size="small" onClick={onSeed} disabled={seeding}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
              {seeding ? 'Seeding…' : 'Seed'}
            </Button>
          )}
          {canWrite && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
              Add Category
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: f.xs }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontSize: f.xs }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={getBrandedTableHeadSx(theme)}>
            <TableRow sx={{ '& th': { fontSize: f.xs } }}>
              <TableCell>Name</TableCell>
              <TableCell align="center">Services</TableCell>
              {canWrite && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={canWrite ? 3 : 2} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" sx={{ fontSize: f.sm }}>
                    No categories yet. Add one, or use Seed Categories to add the standard UAE categories.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {categories.map(cat => {
              const inUse = (cat.service_count || 0) > 0;
              return (
                <TableRow key={cat.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: f.sm }}>{cat.name}</Typography>
                    {cat.description && (
                      <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>{cat.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: f.sm }}>{cat.service_count || 0}</Typography>
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(cat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={inUse ? 'Move its services to another category before deleting' : 'Delete'}>
                        {/* span keeps the tooltip working on a disabled button */}
                        <span>
                          <IconButton size="small" color="error" disabled={inUse} onClick={() => setDeleteTarget(cat)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CategoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        category={editTarget}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: f.sm }}>Delete Category</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: f.sm }}>Delete "{deleteTarget?.name}"? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ fontSize: f.xs }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} sx={{ fontSize: f.xs }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceCategories;
